import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalService } from '@assetaccountant/core/services/modal.service';

import { OperationResponse } from '@assetaccountant/api/models/generic/operation-response';
import { RegisterPermissionType } from '@assetaccountant/api/models/register-permission-type';
import { AuthorizationService } from '@assetaccountant/core/services/authorization.service';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ConnectIntegrationResponse } from '../models/common/connect-integration-response';
import { OidcStateParams } from '../models/oidc-state-params';
import { ConnectXeroCommand } from '../models/xero/connect-xero-command';
import { ListXeroTenantsResponse } from '../models/xero/list-xero-tenants.response';

export const xeroConfig = {
  responseType: 'code',
  scope: environment.xero.scope,
  clientId: environment.xero.clientId,
  redirectUri: environment.xero.redirectUri,
  issuer: environment.xero.issuer,
};

@Injectable({
  providedIn: 'root',
})
export class XeroService {
  constructor(
    private http: HttpClient,
    private modalService: ModalService,
    private authorizationService: AuthorizationService,
  ) {}

  beginConnect(returnRoute: string): Observable<any> {
    return new Observable(subscriber => {
      this.getConnectInstructions().subscribe({
        next: response => {
          this.initiateOAuth(returnRoute);
          subscriber.next(response);
          subscriber.complete();
        },
        error: error => {
          this.handleMfaError(error);
          subscriber.error(error);
        },
      });
    });
  }

  private initiateOAuth(returnRoute: string) {
    const state = this.generateState(returnRoute);

    const url =
      xeroConfig.issuer +
      '?response_type=' +
      encodeURIComponent(xeroConfig.responseType) +
      '&client_id=' +
      encodeURIComponent(xeroConfig.clientId) +
      '&state=' +
      encodeURIComponent(state) +
      '&nonce=' +
      encodeURIComponent(state) +
      '&redirect_uri=' +
      encodeURIComponent(xeroConfig.redirectUri) +
      '&scope=' +
      encodeURIComponent(xeroConfig.scope);

    window.location.href = url;
  }

  private generateState(returnRoute: string) {
    const stateParams = new OidcStateParams();
    stateParams.returnRoute = returnRoute;

    const state: string = btoa(JSON.stringify(stateParams));

    localStorage.setItem('aa-oidc-state', state);

    return state;
  }

  getConnectInstructions() {
    return this.http.get<OperationResponse<void>>(
      `${environment.servicesApiUrl}/api/v2/integrations/xero/connect`,
      {},
    );
  }

  connect(command: ConnectXeroCommand) {
    return this.http.post<ConnectIntegrationResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/xero`,
      command,
    );
  }

  listTenants(authorizationCode: string) {
    return this.http.get<ListXeroTenantsResponse>(
      `${environment.servicesApiUrl}/api/v2/integrations/xero/tenants?authorizationCode=${authorizationCode}`,
    );
  }

  handleError(registerId, httpError, returnRoute, metadata?) {
    const returnUrl = new URL(returnRoute, location.origin);

    if (!returnUrl.searchParams.has('tenantId') && metadata && metadata.TenantId) {
      returnUrl.searchParams.append('tenantId', metadata.TenantId);
    }

    if (this.handleMfaError(httpError)) {
      return;
    }

    // Permission issue
    if (httpError.error?.errorId === '23') {
      this.modalService
        .showQuestionModal(
          '<p>We were unable to complete this action in Xero because the Xero account used to connect AssetAccountant to Xero does not have the required permissions.</p> <p>To resolve this, you can reconnect with a Xero account who has the required permissions or ask your Xero Administrator to grant the additional permissions to the connected account.</p>',
          'Authorization Failure',
          'Reconnect',
          'OK',
          false,
        )
        .subscribe(_ => this.initiateOAuth(returnUrl.toString()));
      return;
    }

    if (
      httpError.status === 403 ||
      (Array.isArray(httpError.error) &&
        (httpError.error[0].message === 'invalid_grant' ||
          httpError.error[0].message === 'Forbidden'))
    ) {
      if (
        registerId &&
        this.authorizationService.isRegisterAuthorized(
          registerId,
          RegisterPermissionType.CreateIntegrationConnection,
        )
      ) {
        this.modalService
          .showErrorModal(
            'We were unable to authenticate with Xero, please reconnect and try again.',
            'Authentication Failure',
            'Reconnect',
          )
          .subscribe(_ => this.initiateOAuth(returnUrl.toString()));
      } else {
        this.modalService.showErrorModal(
          'We were unable to authenticate with Xero, please ask an administrator to reconnect this register',
          'Authentication Failure',
          'OK',
        );
      }
    } else {
      throw httpError;
    }
  }

  private handleMfaError(httpError: any) {
    if (httpError.error?.code !== 'UserMfaRequired') {
      return false;
    }

    const identityServerUrl = environment.openIdConnect.issuer;
    const manageMfaUrl = `${identityServerUrl}/Account/Manage/ManageTwoFactorAuthentication`;

    const modalMessage = `<p>Xero requires multi-factor authentication in order to connect.</p>
      <p><a href="${manageMfaUrl}" target="_blank">Please follow this link to set up multi-factor authentication</a>.</p>
      <p>Once you have done so, make sure to logout of FinQuery Fixed Assets and login again.</p>`;

    this.modalService.showErrorModal(
      modalMessage,
      'Multi-Factor Authentication Required',
      'Dismiss',
    );

    return true;
  }
}
